import React, { useState } from 'react';
import { Phone, Mail, User, MessageSquare, Send, Timer, Clock } from 'lucide-react';
import { company } from '../mock';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ nome: '', telefono: '', email: '', messaggio: '' });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Errore invio');
      const data = await res.json();
      if (window.trackAction) window.trackAction('contact_form_submit');
      toast({
        title: 'Richiesta inviata con successo!',
        description: data.email_sent
          ? 'Abbiamo ricevuto la tua richiesta. Ti contatteremo entro 30 minuti.'
          : 'Richiesta registrata. Ti contatteremo al più presto.',
      });
      setForm({ nome: '', telefono: '', email: '', messaggio: '' });
    } catch (err) {
      toast({
        title: 'Errore invio',
        description: "Riprova o chiamaci direttamente al 091 647 04 59",
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    { icon: Timer, title: 'Intervento in 3 ore', desc: 'Arriviamo rapidamente ovunque nel Ticino' },
    { icon: Clock, title: 'Disponibilità 24/7', desc: 'Sempre pronti per le emergenze' },
  ];

  return (
    <section id="contatti" className="py-20 md:py-28 bg-brand-navy relative overflow-hidden">
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: "radial-gradient(circle at 20% 30%, white 1px, transparent 1px), radial-gradient(circle at 80% 70%, white 1px, transparent 1px)",
        backgroundSize: '60px 60px'
      }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-display text-3xl md:text-5xl font-bold text-center text-white uppercase">
          Hai un&rsquo;emergenza?<br className="md:hidden" /> Contattaci Subito
        </h2>
        <div className="w-20 h-1 bg-brand-red mx-auto mt-4" />
        <p className="text-center text-white/80 mt-6 max-w-2xl mx-auto">
          Il nostro team è disponibile 24 ore su 24, 7 giorni su 7 per interventi urgenti in tutto il Ticino.
        </p>

        <div className="mt-14 grid lg:grid-cols-2 gap-10">
          <div>
            <div className="space-y-6">
              {benefits.map((b) => {
                const Icon = b.icon;
                return (
                  <div key={b.title} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-brand-red/20 border border-brand-red flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-brand-red" />
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-white uppercase tracking-wide">{b.title}</h3>
                      <p className="text-sm text-white/70 mt-1">{b.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <a href={`tel:${company.phoneRaw}`} className="mt-10 inline-flex items-center gap-3 bg-brand-red hover:bg-brand-red-dark text-white px-8 py-5 rounded-md font-bold transition-all shadow-xl hover:-translate-y-0.5">
              <Phone className="w-6 h-6" />
              <span className="font-display text-2xl tracking-wide">{company.phone}</span>
            </a>
          </div>

          <form onSubmit={submit} className="bg-white rounded-xl shadow-2xl p-7 md:p-9" aria-labelledby="contact-form-title">
            <h3 id="contact-form-title" className="font-display text-xl md:text-2xl font-bold text-brand-navy uppercase mb-6">Richiedi un Intervento Ora</h3>
            <div className="space-y-4">
              <div className="relative">
                <label htmlFor="cf-nome" className="sr-only">Nome</label>
                <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" aria-hidden="true" />
                <input id="cf-nome" required name="nome" value={form.nome} onChange={onChange} type="text" placeholder="Nome" autoComplete="name" className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-brand-red" />
              </div>
              <div className="relative">
                <label htmlFor="cf-telefono" className="sr-only">Telefono</label>
                <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" aria-hidden="true" />
                <input id="cf-telefono" required name="telefono" value={form.telefono} onChange={onChange} type="tel" placeholder="Telefono" autoComplete="tel" className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-brand-red" />
              </div>
              <div className="relative">
                <label htmlFor="cf-email" className="sr-only">Email</label>
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" aria-hidden="true" />
                <input id="cf-email" required name="email" value={form.email} onChange={onChange} type="email" placeholder="Email" autoComplete="email" className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-brand-red" />
              </div>
              <div className="relative">
                <label htmlFor="cf-messaggio" className="sr-only">Descrizione del problema</label>
                <MessageSquare className="w-4 h-4 absolute left-3 top-4 text-gray-500 pointer-events-none" aria-hidden="true" />
                <textarea id="cf-messaggio" required name="messaggio" value={form.messaggio} onChange={onChange} rows={4} placeholder="Descrizione del Problema" className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-brand-red resize-none" />
              </div>
              <button type="submit" disabled={loading} aria-busy={loading} className="w-full bg-brand-red hover:bg-brand-red-dark disabled:opacity-60 text-white font-bold uppercase tracking-wide py-3.5 rounded-md transition-colors flex items-center justify-center gap-2 focus:outline-none focus:ring-4 focus:ring-brand-red/30">
                {loading ? 'Invio in corso…' : (<><Send className="w-4 h-4" aria-hidden="true" /> Invia Richiesta</>)}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
